/**
 * ================================================================================
 * COMPONENTE: NotificationPanel
 * PROPÓSITO: Painel deslizante de notificações (lado direito)
 * ================================================================================
 */
import React from 'react';
import { X, AlertTriangle, CheckCircle, Info, XCircle, BellOff } from 'lucide-react';
import { useFinance } from '../../context/FinanceContext';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';

const typeIcons = {
  warning: AlertTriangle,
  success: CheckCircle,
  info: Info,
  error: XCircle,
};

const typeColors = {
  warning: { bg: 'rgba(245,158,11,0.15)', color: '#F59E0B' },
  success: { bg: 'rgba(16,185,129,0.15)', color: '#10B981' },
  info:    { bg: 'rgba(79,142,247,0.15)', color: '#4F8EF7' },
  error:   { bg: 'rgba(239,68,68,0.15)', color: '#EF4444' },
};

export default function NotificationPanel({ onClose }) {
  const { notifications, unreadCount, markNotificationRead } = useFinance();

  return (
    <>
      <div
        style={{ position: 'fixed', inset: 0, zIndex: 55, background: 'rgba(0,0,0,0.3)' }}
        onClick={onClose}
      />
      <div className="notif-panel">
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px', borderBottom: '1px solid var(--border-primary)' }}>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 700 }}>Notificações</h2>
            {unreadCount > 0 && (
              <span style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>
                {unreadCount} não {unreadCount === 1 ? 'lida' : 'lidas'}
              </span>
            )}
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Lista */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {notifications.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">
                <BellOff size={28} />
              </div>
              <p className="empty-state-title">Sem notificações</p>
              <p className="empty-state-text">Não tem notificações de momento.</p>
            </div>
          ) : (
            notifications.map((notif) => {
              const Icon = typeIcons[notif.type] || Info;
              const colors = typeColors[notif.type] || typeColors.info;

              return (
                <div
                  key={notif.id}
                  className={`notif-item ${!notif.is_read ? 'unread' : ''}`}
                  onClick={() => !notif.is_read && markNotificationRead(notif.id)}
                >
                  <div style={{
                    width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                    background: colors.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Icon size={18} color={colors.color} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
                      {notif.title}
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--text-tertiary)', marginTop: 2 }}>
                      {notif.message}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                      {format(new Date(notif.created_at), "d MMM 'às' HH:mm", { locale: pt })}
                    </div>
                  </div>
                  {!notif.is_read && (
                    <div style={{
                      width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                      background: 'var(--accent-blue)',
                    }} />
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </>
  );
}
